import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminCancellation } from '../../services/cancellationsApi';

const tabs = [
  { key: 'all', label: 'All', status: '' },
  { key: 'review', label: 'Needs Review', status: 'requested' },
  { key: 'approved', label: 'Approved', status: 'approved' },
  { key: 'initiated', label: 'Refund Initiated', status: 'refund_initiated' },
  { key: 'refunded', label: 'Refunded', status: 'refunded' },
  { key: 'disputed', label: 'Disputed', status: 'disputed' },
];

const toneMap = {
  requested: 'bg-amber-50 text-amber-700 ring-amber-100',
  host_reviewed: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  admin_reviewed: 'bg-blue-50 text-blue-700 ring-blue-100',
  approved: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  rejected: 'bg-red-50 text-red-700 ring-red-100',
  refund_initiated: 'bg-sky-50 text-sky-700 ring-sky-100',
  refunded: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  disputed: 'bg-orange-50 text-orange-700 ring-orange-100',
};

const StatusChip = ({ status }) => {
  const cls = toneMap[status] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>{status || 'unknown'}</span>;
};

const AdminCancellations = ({ token }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({ status: '', q: '', date_from: '', date_to: '' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminCancellation.list(token, filters);
      setItems(res.items || res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load cancellations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  const handleTab = (tab) => {
    setActiveTab(tab.key);
    setFilters((f) => ({ ...f, status: tab.status || '' }));
  };

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Operations</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Cancellations & Refunds</h1>
          <p className="text-sm text-slate-600">Platform-wide visibility into cancellation requests and refunds.</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTab(tab)}
            className={`rounded-full px-4 py-2 text-sm font-semibold ${
              activeTab === tab.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <input
          type="search"
          placeholder="Search booking or guest"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={load}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Booking</th>
              <th className="px-4 py-3">Property</th>
              <th className="px-4 py-3">Guest</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Refund</th>
              <th className="px-4 py-3">Requested</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {loading && (
              <tr>
                <td className="px-4 py-3 text-slate-500" colSpan={7}>
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td className="px-4 py-3 text-red-600" colSpan={7}>
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td className="px-4 py-3 text-slate-600" colSpan={7}>
                  No cancellation requests found.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{item.booking_code}</td>
                <td className="px-4 py-3 text-slate-700">{item.property_title}</td>
                <td className="px-4 py-3 text-slate-700">{item.guest_name || 'Guest'}</td>
                <td className="px-4 py-3">
                  <StatusChip status={item.status} />
                </td>
                <td className="px-4 py-3 font-semibold text-slate-900">
                  {item.currency} {item.refund_amount || 0}
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {item.requested_at ? new Date(item.requested_at).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/cancellations/${item.id}`)}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCancellations;
